import autobind from "autobind-decorator";
import * as QRCode from "qrcode.react";
import * as React from "react";
import ProgressiveImage from "react-progressive-image-loading";
import StripeCheckout, { Token } from "react-stripe-checkout";

import { CircularProgress, Dialog, DialogContent, DialogContentText, DialogTitle, WithStyles } from "@material-ui/core";
import { StyleRules, Theme, withStyles } from "@material-ui/core/styles";

import API from "./API";
import Button from "./Button";
import FS from "./FS";
import { formatPrice, Kit } from "./kits";
import promo from "./Promo"
import RowList from "./RowList";
import Text from "./Text";
import TryWithExpo from "./TryWithExpo";

type ClassNames = "container" | "left" | "right" | "expo" | "btns" | "qrCode" | "link" | "video";

declare function goes_to_nativebase_conversion(href?: string): void;

const styles = (theme: Theme): StyleRules<ClassNames> => ({
    container: {
        [theme.breakpoints.down("md")]: {
            minHeight: 0,
            flexDirection: "column !important" as "column",
            marginBottom: 100
        }
    },
    left: {
        width: "50vw",
        boxSizing: "border-box",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        overflow: "hidden",
        [theme.breakpoints.down("md")]: {
            width: "100%",
            height: "62vw",
            marginTop: theme.spacing.unit * 2,
            marginBottom: theme.spacing.unit * 2
        }
    },
    video: {
        height: "100%",
        width: "100%",
        objectFit: "cover"
    },
    right: {
        width: "50vw",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        paddingLeft: theme.spacing.unit * 4,
        paddingRight: theme.spacing.unit * 4,
        [theme.breakpoints.down("md")]: {
            width: "100%",
            flexDirection: "column"
        }
    },
    btns: {
        marginTop: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 2
    },
    expo: {
        display: "flex",
        [theme.breakpoints.down("md")]: {
            alignItems: "center"
        }
    },
    qrCode: {
        marginLeft: theme.spacing.unit * 2
    },
    link: {
        color: theme.typography.body1.color
    }
});

interface KitOverviewProps {
    kit: Kit,
    specialPromo?: boolean,
    reverse?: boolean
}

interface KitOverviewState {
    loading: boolean,
    error: boolean,
    data: { url: string } | undefined
}

const isProduction = () => process.env.NODE_ENV === "production";

class KitOverview extends React.Component<WithStyles<ClassNames> & KitOverviewProps, KitOverviewState> {

    private key: string = isProduction()
        ?
            "pk_live_2ruvnlX2Eqa1yYxXjufnGiKA"
        :
            "pk_test_IgNcZsyRG4FkepgyUGEBXAn4";

    public componentWillMount() {
        this.setState({ loading: false, error: false });
    }

    public renderImage = (src: string, style: React.CSSProperties) => (
        <div
            className={this.props.classes.left}
            style={Object.assign(style, { backgroundImage: `url(${src})` })}
        />
    )

    public render() {
        const {classes, kit, reverse, specialPromo} = this.props;
        const {loading, error, data} = this.state;
        const price =
          specialPromo ? { value: kit.id === "elements" ? 1950 : 5000, currency: "USD" as "USD" } : kit.price;
        const containerStyle: React.CSSProperties = {
            flexDirection: reverse ? "row-reverse" : "row"
        };
        return (
            <FS className={classes.container} style={containerStyle} id={kit.id}>
                {
                    !kit.video && (
                        <ProgressiveImage
                            preview={kit.thumbnailPreview}
                            src={kit.thumbnail}
                            render={this.renderImage}
                        />
                    )
                }
                {
                    kit.video && (
                        <div className={classes.left}>
                            <video className={classes.video} autoPlay={true} loop={true} muted={true}>
                                <source src={kit.video} type="video/mp4" />
                            </video>
                        </div>
                    )
                }
                <div className={classes.right}>
                    <Text variant="title2">{kit.name}</Text>
                    <Text variant="title3">{kit.overview}</Text>
                    <Text>
                    {kit.description}
                    </Text>
                    {
                        (!promo.active && kit.price.originalPrice) && (
                            <Text variant="important">
Single license - {formatPrice({ value: kit.price.originalPrice, currency: kit.price.currency })}
                            </Text>
                        )
                    }
                    {
                        (!promo.active && !kit.price.originalPrice) && (
                            <Text variant="important">
                                Single license - {formatPrice(kit.price)}
                            </Text>
                        )
                    }
                    {
                        (promo.active && kit.id === "elements") && (
                            <Text variant="important">
                                Save 75% until {promo.ends}: <span>{formatPrice(kit.price)}</span>
                            </Text>
                        )
                    }
                    <div className={classes.btns}>
                        <RowList>
                        {
                            kit.buy && (
                                <Button
                                    color={kit.color}
                                    href={kit.buy}
                                    // tslint:disable-next-line:jsx-no-lambda
                                    onClick={() => goes_to_nativebase_conversion(kit.buy)}
                                    target={kit.id === "elements" ? "_self" : "_blank"}
                                >
                                Learn more
                                </Button>
                            )
                        }
                        {
                            !kit.buy && (
                                <StripeCheckout
                                     ComponentClass="span"
                                     token={this.onToken}
                                     stripeKey={this.key}
                                     amount={price.value}
                                     currency={price.currency}
                                >
                                    <Button>
                                    {formatPrice(price)} Buy Now
                                    </Button>
                                </StripeCheckout>
                            )
                        }
                        </RowList>
                    </div>
                    {
                        kit.testFlight && (
                                <div className={classes.expo}>
                                    <TryWithExpo
                                        name={kit.name}
                                        expo={kit.expo}
                                        color={kit.color}
                                        testFlight={kit.testFlight}
                                        apk={kit.apk}
                                    />
                                </div>
                        )
                    }
                    {
                        !kit.testFlight && (
                            <div className={classes.expo}>
                                <QRCode value={kit.expo} fgColor="black" />
                                <div className={classes.qrCode}>
                                    <Text variant="title3">Try with Expo</Text>
                                    <Text>
                                    Scanning this QR code with your Expo mobile app loads this experience immediately.
                                    </Text>
                                </div>
                            </div>
                        )
                    }
                </div>

                <Dialog open={loading}>
                    <DialogContent>
                        <CircularProgress />
                    </DialogContent>
                </Dialog>

                <Dialog open={error}>
                    <DialogTitle>An error happened</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                        An unknown error happened.
                        Please <a href="mailto:wcandillon@gmail.com" className={classes.link}>contact us</a>.
                        </DialogContentText>
                    </DialogContent>
                </Dialog>

                <Dialog open={!!data} onClose={this.closeDialog}>
                    <DialogTitle>Thank you for your purchase</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
Thank you for your purchase ❤️.
You can download the package <a className={classes.link} href={data && data.url}>here</a>.
Please checkout the project documentation.
If you have any questions, please <a className={classes.link} href="mailto:wcandillon@gmail.com">contact us</a>.
                        </DialogContentText>
                    </DialogContent>
                </Dialog>
            </FS>
        );
    }

    @autobind
    private async onToken(token: Token) {
        const {specialPromo, kit} = this.props;
        const {endpoint} = API;
        const fn = "getPackage";
        const testMode = !isProduction();
        this.setState({ loading: true });
        try {
            const response = await fetch(`${endpoint}${fn}?testMode=${testMode}&promo=${!!specialPromo}&id=${kit.id}`, {
                method: "POST",
                body: JSON.stringify(token),
                headers: {
                    "Accept": "application/json, text/plain, */*",
                    "Content-Type": "application/json"
                }
            });
            if (response.status === 200) {
                const data = await response.json();
                this.setState({ loading: false, data });
            } else {
                this.setState({ loading: false, error: true });
            }
        } catch (e) {
            this.setState({ loading: false, error: true });
        }
    }

    @autobind
    private closeDialog() {
        this.setState({ data: undefined });
    }
}

export default  withStyles(styles)<KitOverviewProps>(KitOverview);



// WEBPACK FOOTER //
// ./src/components/KitOverview.tsx